import type { ObjectGraph } from '@jet/environment/dependencies';
import { Bag } from '../../dependencies/bag';

/**
 * Shared method to generate URL for a Classical Resource to be used as href on Lockup Links
 * @param type Type of the Classical Resource, ex: album, instrument etc
 * @param id ID of the Classical Resource
 * @param objectGraph Object Graph dependency
 * @returns url
 */
export function getUrl(
    type: string,
    actionUrl: string,
    objectGraph: ObjectGraph,
): string {
    const countryCode = Bag.countryCode(objectGraph);
    const id = getId(actionUrl);

    return id ? `/${countryCode}/${type}/${id}` : null;
}

/**
 * Utility to grab the route segment from a given URL.
 *
 * @param {string} url The url to extract the route segment from
 * @returns {string | null}
 */
export function getRouteSegment(url?: string): string | null {
    if (!url) {
        return null;
    }

    // Ensure we're working with consistent URL structures.  Function may
    // be used to pass in both modified/unmodified urls.
    return url.replace('/query/view/', '/').split('/')[2];
}

/**
 * Shared method to get ID for a Classical Resource
 * @param actionUrl Query URL for resource
 * @returns id
 */
export function getId(actionUrl: string): string {
    const entityIdRegex = /view\/[^/]+\/([^/]+)\/([^/?]+)/;
    const matches = entityIdRegex.exec(actionUrl || '');
    return matches?.[2];
}

const LIBRARY_ENTITY_MAPPING = {
    'library-playlist': 'library/playlist',
    'library-album': 'library/album',
};

/**
 * Shared method to transform action URLs for a Classical Resource to be
 * used as href on lockups on See All pages.
 *
 * - Removes the '/query/view' portion of the url,
 * - Removes any query params that might come along with it
 *
 * NOTE: The only urls that really need to be absolute urls are links
 *       tagged with `rel="canonical"`.  Relative urls are preferred
 *       to allow the links to have the context of their current environment
 *       (carry, stage, etc.)
 *
 * @method transformActionUrl
 * @param {[string]} actionUrl the url string to transform
 * @param {boolean} [makeCanonical=false] makeCanonical generate a full url (true) or just the path (false)
 * @returns {string | undefined} url path
 *
 * @example
 * ```
 * transformActionUrl('/query/view/us/artist/306830056')
 * > '/us/artist/306830056'
 * ```
 *
 * @example
 * ```
 * transformActionUrl('/query/view/us/album/1785891869?originalQuery=', true)
 * > 'https://classical.music.apple.com/us/album/1785891869'
 * ```
 */
export function transformActionUrl(
    actionUrl?: string,
    makeCanonical: boolean = false,
): string | undefined {
    if (!actionUrl) {
        return undefined;
    }

    const qpIndex =
        actionUrl.indexOf('?') > -1 ? actionUrl.indexOf('?') : actionUrl.length;

    let path = actionUrl.slice(0, qpIndex).replace('/query/view/', '/');
    const routeSegment = getRouteSegment(path);

    if (routeSegment.startsWith('library-')) {
        // Remove the storefront from library url paths
        const storefrontlessPath = path.slice(path.indexOf('/library'));
        // Match content between the first two `/` instances
        const regex = /\/(.*?)\//;
        const match = storefrontlessPath.match(regex);
        // Map the matching entity without surrounding `/` to a known list
        const entity = LIBRARY_ENTITY_MAPPING[match[1]];

        if (entity) {
            path = storefrontlessPath.replace(match[1], entity);
        } else {
            // rdar://146311664 ([Classical on Web]: transformActionUrl - update function to handle library action urls)
            return undefined;
        }
    } else if (routeSegment === 'browse') {
        if (path.endsWith('/artists')) {
            path = path.slice(0, path.indexOf('/artists'));
        } else if (path.endsWith('/composer/composers')) {
            path = path.replace('/composer/composers', '/composers');
        }
    } else if (routeSegment === 'editorial-playlist-container') {
        path = path.replace(
            '/editorial-playlist-container',
            '/browse/playlists',
        );
    } else if (routeSegment === 'periods') {
        path = path.replace('/periods', '/browse/periods');
    } else if (routeSegment === 'genres') {
        path = path.replace('/genres', '/browse/genres');
    }

    if (!makeCanonical) {
        return path;
    }

    return `https://classical.music.apple.com${path}`;
}
